/**
 * Normaliza solicitações do GET /clients/:id/details para o componente History (chat).
 * Compatível com payload enriquecido (API nova) e legado (id, subject, status, lastUpdate).
 */

export function parseProtocolSubject(text) {
  if (text == null || text === "") return "";
  const lines = String(text)
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length >= 2) return lines.slice(1).join(" ");
  const one = lines[0] || "";
  return one.replace(/^Protocolo:\s*\d+\s*/i, "").trim();
}

function buildSubjectFromTypes(r) {
  const parts = [r.typeName, r.subtype1Name, r.subtype2Name, r.subtype3Name]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean);
  return parts.length ? parts.join(" • ") : "";
}

function resolveAssignee(r) {
  if (r.assignee?.label) return r.assignee;
  const agentId = r.agentId != null ? String(r.agentId).trim() : "";
  if (agentId && agentId !== "0" && agentId !== "00000000-0000-0000-0000-000000000000") {
    return { type: "agent", label: r.agentName || "Agente de IA" };
  }
  const userId = r.userId != null ? Number(r.userId) : 0;
  if (userId > 0) {
    return {
      type: Number(r.userUra) === 1 ? "chatbot" : "user",
      label: r.userName || "Atendente",
      isChatBot: Number(r.userUra) === 1,
    };
  }
  return { type: "queue", label: "Fila" };
}

function resolveLastMessage(r, subject) {
  if (r.lastMessagePreview?.text && r.lastMessagePreview.text !== "—") {
    return r.lastMessagePreview;
  }
  if (r.lastMessage && r.lastMessage !== "—" && r.lastMessage !== "Sem mensagens no histórico") {
    return { text: r.lastMessage, kind: "text" };
  }
  const rawProduto = r.produto != null ? String(r.produto).trim() : "";
  const previewFromProduto = parseProtocolSubject(rawProduto);
  if (previewFromProduto && previewFromProduto !== subject) {
    return { text: previewFromProduto, kind: "text" };
  }
  const channel = r.channel != null ? String(r.channel).trim() : "";
  if (channel) return { text: channel, kind: "text" };
  return null;
}

export function normalizeClientRequestForHistory(r) {
  if (!r?.id) return r;

  if (r.restricted) {
    const subject = r.subject || buildSubjectFromTypes(r) || "Tipo restrito";
    const timestamp = r.timestamp || r.lastUpdate || r.date;
    return {
      id: r.id,
      restricted: true,
      restrictedReason: r.restrictedReason || "type",
      subject,
      description: subject,
      timestamp,
      date: r.date || timestamp,
      lastUpdate: r.lastUpdate || timestamp,
      typeName: r.typeName || r.tipo_nome || null,
      subtype1Name: r.subtype1Name || r.subtipo1_nome || null,
      subtype2Name: r.subtype2Name || r.subtipo2_nome || null,
      subtype3Name: r.subtype3Name || r.subtipo3_nome || null,
      status: null,
      channel: null,
      channelType: null,
      channel_type: null,
      channelId: null,
      userId: null,
      userName: null,
      userUra: 0,
      agentId: null,
      agentName: null,
      assignee: null,
      lastMessage: null,
      lastMessagePreview: null,
    };
  }

  const fromTypes = buildSubjectFromTypes(r);
  const fromProduto = parseProtocolSubject(r.produto);
  const fromSubjectField = parseProtocolSubject(r.subject);
  const subject =
    fromTypes ||
    (fromSubjectField && fromSubjectField !== "Solicitação" ? fromSubjectField : "") ||
    fromProduto ||
    "Solicitação";

  const timestamp = r.timestamp || r.lastUpdate || r.date;
  const lastMessagePreview = resolveLastMessage(r, subject);
  let lastMessage = lastMessagePreview?.text || r.lastMessage || "";
  if (!lastMessage || lastMessage === "—") {
    const channel = r.channel != null ? String(r.channel).trim() : "";
    lastMessage = channel || "Sem mensagens no histórico";
  }

  const channelType = r.channelType ?? r.channel_type ?? null;

  return {
    id: r.id,
    status: r.status,
    subject,
    description: r.description || `Protocolo: ${r.id}\n${subject}`,
    timestamp,
    date: r.date || timestamp,
    lastUpdate: r.lastUpdate || timestamp,
    channel: r.channel ?? null,
    channelType,
    channel_type: channelType,
    channelId: r.channelId ?? null,
    userId: r.userId ?? null,
    userName: r.userName ?? null,
    userUra: r.userUra != null ? Number(r.userUra) : 0,
    agentId: r.agentId ?? null,
    agentName: r.agentName ?? null,
    assignee: resolveAssignee(r),
    lastMessage,
    lastMessagePreview: lastMessagePreview || (lastMessage ? { text: lastMessage, kind: "text" } : null),
  };
}

export function normalizeClientRequestsForHistory(requests) {
  return (Array.isArray(requests) ? requests : []).map(normalizeClientRequestForHistory);
}

const REQ_STATUS_LABELS = {
  0: "Pendente",
  1: "Em Atendimento",
  2: "Concluída",
  3: "Contato Futuro",
  4: "Perdida",
  5: "Cancelada",
};

export function getClientRequestStatusLabel(status) {
  if (status == null || status === "") return "—";
  const n = Number(status);
  if (Number.isFinite(n) && n in REQ_STATUS_LABELS) return REQ_STATUS_LABELS[n];
  return String(status);
}

function formatExportDate(value) {
  if (!value) return "—";
  const raw = String(value).trim();
  const mysqlMatch = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(raw);
  if (mysqlMatch) {
    const [, year, month, day, time] = mysqlMatch;
    return `${day}/${month}/${year} ${time}`;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return raw || "—";
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function sanitizeExportText(text) {
  if (!text) return "";
  return String(text)
    .replace(/<[^>]*>/g, "")
    .trim();
}

function joinSubtypeNames(row) {
  return [row?.subtype1Name, row?.subtype2Name, row?.subtype3Name]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean)
    .join(" / ");
}

/**
 * Linhas no formato da busca, para a barra de unificar/exportar na ficha do cliente.
 */
export function mapClientRequestsToExportRows(requests, { clientName = "" } = {}) {
  const source = Array.isArray(requests) ? requests : [];
  return source
    .filter((r) => r?.id != null && !r.restricted)
    .map((original) => {
      const normalized = normalizeClientRequestForHistory(original);
      const subtypesLabel = joinSubtypeNames(original);
      const lastMessage =
        sanitizeExportText(normalized.lastMessage) ||
        sanitizeExportText(original.lastMessage) ||
        "";
      return {
        ...original,
        ...normalized,
        name: clientName || original.name || "",
        assignedTo: normalized.assignee?.label || original.userName || "Fila",
        lastMessage,
        createdAt: original.date || original.createdAt || normalized.date,
        lastUpdate:
          original.lastUpdate || original.timestamp || normalized.lastUpdate || normalized.timestamp,
        typeName: original.typeName || normalized.typeName || "",
        subtypesLabel: subtypesLabel || "—",
        statusLabel: getClientRequestStatusLabel(normalized.status ?? original.status),
        channel: original.channel || normalized.channel || "",
      };
    });
}

export const CLIENT_REQUEST_EXPORT_COLUMNS = [
  {
    field: "id",
    headerName: "Protocolo",
    valueGetter: (value) => value ?? "",
  },
  {
    field: "name",
    headerName: "Nome",
    valueGetter: (value, row) => value || row?.name || "",
  },
  {
    field: "assignedTo",
    headerName: "Responsável",
    valueGetter: (value, row) => value || row?.assignedTo || row?.userName || "Fila",
  },
  {
    field: "lastMessage",
    headerName: "Última Mensagem",
    valueGetter: (value, row) => sanitizeExportText(value || row?.lastMessage),
  },
  {
    field: "createdAt",
    headerName: "Data de cadastro",
    valueGetter: (_, row) => formatExportDate(row?.createdAt || row?.date),
  },
  {
    field: "lastUpdate",
    headerName: "Última atualização",
    valueGetter: (_, row) => formatExportDate(row?.lastUpdate || row?.timestamp),
  },
  {
    field: "typeName",
    headerName: "Tipo",
    valueGetter: (value, row) => value || row?.typeName || "—",
  },
  {
    field: "subtypesLabel",
    headerName: "Subtipos",
    valueGetter: (value, row) => value || joinSubtypeNames(row) || "—",
  },
  {
    field: "statusLabel",
    headerName: "Status",
    valueGetter: (value, row) =>
      value || getClientRequestStatusLabel(row?.status) || "—",
  },
  {
    field: "channel",
    headerName: "Canal",
    valueGetter: (value, row) => value || row?.channel || "—",
  },
];
